import { BookOpen, Bot, ListChecks, Mic, Trophy, type LucideIcon } from "lucide-react";

/**
 * The copy and shape of the practice loop: what each stage is called, how
 * long it holds, and the sample content the stages render.
 *
 * This moved out of the marketing site so the auth showcase can run the
 * *same* loop. Two copies of this data would drift, and the one thing the
 * demo must never do is show a product that contradicts itself between the
 * landing page and the sign-up screen.
 */

export type LoopStageId =
  "topic" | "answer" | "feedback" | "words" | "progress";

export interface LoopStage {
  id: LoopStageId;
  /** Nav label — short enough for a chip or a ring node. */
  label: string;
  /** What the stage is, in the product's own words. */
  headline: string;
  /** One sentence of information, shown under the loop. */
  caption: string;
  icon: LucideIcon;
  /** Milliseconds this stage holds before the loop advances. */
  dwell: number;
}

export const PRACTICE_LOOP: LoopStage[] = [
  {
    id: "topic",
    label: "Pick a topic",
    headline: "A topic, not a textbook.",
    caption:
      "Browse curated topics or describe what you want to practise. The AI writes the lesson around you.",
    icon: BookOpen,
    dwell: 6200,
  },
  {
    id: "answer",
    label: "Answer out loud",
    headline: "One question, one voice note.",
    caption:
      "Read the prompt, hold the button, talk for as long as you have something to say. Then listen back.",
    icon: Mic,
    dwell: 3800,
  },
  {
    id: "feedback",
    label: "Read the feedback",
    headline: "Three notes, not a wall of red.",
    caption:
      "Grammar, vocabulary and pronunciation — one line each, with the sentence you actually said.",
    icon: Bot,
    dwell: 3400,
  },
  {
    id: "words",
    label: "Save the words",
    headline: "New words land in your deck.",
    caption:
      "Anything you hesitated on is saved with its tier, and comes back in a later session.",
    icon: ListChecks,
    dwell: 3200,
  },
  {
    id: "progress",
    label: "Watch the streak",
    headline: "Proof that you showed up.",
    caption:
      "Sessions, words and days in a row, in one quiet strip. Small wins, stacked over weeks.",
    icon: Trophy,
    dwell: 3400,
  },
];

/**

 * The copy for the loop's first beat, which shows a topic being
 * *created* rather than chosen: you describe what you want, the AI
 * writes the lesson. `PROMPT_CHARS` feeds the typing animation's
 * `steps()` count, so the caret lands exactly on the last character.
 */
export const CREATE_TOPIC = {
  heading: "What will you practice today?",
  placeholder: "Describe any topic…",
  prompt: "Greener cities and getting around",
  status: "Writing your questions and vocabulary",
  done: "Ready when you are",
  questionCount: 6,
  wordCount: 12,
  firstQuestion:
    "What small change could your city make this year to cut car trips in half?",
};

export const PROMPT_CHARS = CREATE_TOPIC.prompt.length;

/** Hand-shaped bar heights so the waveform reads as speech, not noise. */
export const LOOP_WAVEFORM = [
  10, 18, 26, 14, 34, 22, 40, 18, 30, 46, 24, 36, 16, 28, 42, 20, 32, 14, 26,
  38, 18, 28, 12, 20,
];

export const SAMPLE_TOPIC = {
  category: "Environment",
  title: "Greener cities, smaller trips",
  level: "B1",
  wordsSaved: 12,
  questions: 6,
};
